'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { motion, useMotionValue, useTransform, animate } from 'framer-motion'
import {
  ChevronDown,
  FileText,
  Plus,
  Trash2,
  TrendingUp,
  Award,
  DollarSign,
} from 'lucide-react'
import {
  calculateContractValue,
  type ContractInput,
  type ContractResult,
  type ContractPromotion,
  type SpecialPay,
} from '@/app/actions'

/* ── Constants ─────────────────────────────────────────── */
const RANKS = [
  'E-1','E-2','E-3','E-4','E-5','E-6','E-7','E-8','E-9',
  'W-1','W-2','W-3','W-4','W-5',
  'O-1','O-2','O-3','O-4','O-5','O-6','O-7','O-8','O-9','O-10',
]

const CONTRACT_YEARS = [2, 3, 4, 5, 6, 7, 8, 9, 10]

const SERVICE_BRANCHES = [
  { value: 'army', label: 'Army' },
  { value: 'navy', label: 'Navy' },
  { value: 'air_force', label: 'Air Force' },
  { value: 'marines', label: 'Marines' },
  { value: 'coast_guard', label: 'Coast Guard' },
  { value: 'space_force', label: 'Space Force' },
]

const SPECIAL_PAY_PRESETS: { label: string; amount: number; taxFree: boolean }[] = [
  { label: 'Sea Pay', amount: 250, taxFree: false },
  { label: 'Flight Pay', amount: 250, taxFree: false },
  { label: 'Hazardous Duty', amount: 150, taxFree: false },
  { label: 'COLA', amount: 200, taxFree: true },
  { label: 'Combat Pay (HFP)', amount: 225, taxFree: true },
]

/* ── Animated big number ──────────────────────────────── */
function AnimatedBigNumber({ value, prefix = '$' }: { value: number; prefix?: string }) {
  const motionVal = useMotionValue(0)
  const display = useTransform(motionVal, (v) =>
    `${prefix}${Math.round(v).toLocaleString()}`
  )
  const [text, setText] = useState(`${prefix}0`)

  useEffect(() => {
    const unsub = display.on('change', (v) => setText(v))
    return unsub
  }, [display])

  useEffect(() => {
    const ctrl = animate(motionVal, value, { duration: 1.2, ease: [0.16, 1, 0.3, 1] })
    return () => ctrl.stop()
  }, [value, motionVal])

  return <span>{text}</span>
}

/* ── SVG Area Chart ───────────────────────────────────── */
function CumulativeChart({
  result,
  contractYears,
}: {
  result: ContractResult
  contractYears: number
}) {
  const { months } = result
  const totalMonths = months.length
  if (totalMonths === 0) return null

  const maxVal = months[months.length - 1].cumulative
  const W = 700
  const H = 300
  const PADDING = { top: 30, right: 20, bottom: 50, left: 70 }
  const chartW = W - PADDING.left - PADDING.right
  const chartH = H - PADDING.top - PADDING.bottom

  const xScale = (m: number) => PADDING.left + ((m - 1) / (totalMonths - 1)) * chartW
  const yScale = (v: number) => PADDING.top + chartH - (v / maxVal) * chartH

  // Area path
  const linePts = months.map((d) => `${xScale(d.month)},${yScale(d.cumulative)}`).join(' ')
  const areaPath = `M${xScale(1)},${yScale(0)} L${linePts} L${xScale(totalMonths)},${yScale(0)} Z`
  const linePath = `M${linePts}`

  // Y-axis ticks
  const yTicks = 5
  const yTickValues = Array.from({ length: yTicks + 1 }, (_, i) =>
    Math.round((maxVal / yTicks) * i)
  )

  // X-axis ticks — one per year
  const xTickMonths = Array.from({ length: contractYears + 1 }, (_, i) =>
    i === 0 ? 1 : i * 12
  )

  // Events (promotions / bonuses)
  const events = months.filter((d) => d.event)

  const [hoveredMonth, setHoveredMonth] = useState<number | null>(null)
  const hoveredData = hoveredMonth ? months[hoveredMonth - 1] : null

  return (
    <div className="relative w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {yTickValues.map((v) => (
          <line
            key={v}
            x1={PADDING.left}
            x2={W - PADDING.right}
            y1={yScale(v)}
            y2={yScale(v)}
            stroke="var(--border)"
            strokeWidth={0.5}
            opacity={0.6}
          />
        ))}

        {/* Area */}
        <motion.path
          d={areaPath}
          fill="url(#areaGrad)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        />

        {/* Line */}
        <motion.path
          d={linePath}
          fill="none"
          stroke="#3b82f6"
          strokeWidth={2.5}
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
        />

        {/* Event markers — prominent badges for promotions & bonuses */}
        {events.map((evt) => {
          const cx = xScale(evt.month)
          const cy = yScale(evt.cumulative)
          const isBonus = evt.event?.includes('$')
          const color = isBonus ? '#10b981' : '#8b5cf6'
          const labelText = evt.event ?? ''
          // Approximate label width for background pill
          const charW = 5.5
          const pillW = Math.max(labelText.length * charW + 16, 40)
          const pillH = 18
          const labelY = cy - 20

          return (
            <g key={evt.month}>
              {/* Vertical dashed line from marker to axis */}
              <line
                x1={cx}
                y1={cy}
                x2={cx}
                y2={PADDING.top + chartH}
                stroke={color}
                strokeWidth={1}
                strokeDasharray="3 3"
                opacity={0.35}
              />
              {/* Main dot */}
              <circle
                cx={cx}
                cy={cy}
                r={5}
                fill={color}
                stroke="var(--surface-1)"
                strokeWidth={2.5}
              />
              {/* Label background pill */}
              <rect
                x={cx - pillW / 2}
                y={labelY - pillH / 2}
                width={pillW}
                height={pillH}
                rx={9}
                fill={color}
                opacity={0.15}
              />
              {/* Label text */}
              <text
                x={cx}
                y={labelY + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={color}
                fontSize={9}
                fontWeight={700}
                fontFamily="system-ui, sans-serif"
              >
                {labelText}
              </text>
            </g>
          )
        })}

        {/* Y-axis labels */}
        {yTickValues.map((v) => (
          <text
            key={v}
            x={PADDING.left - 8}
            y={yScale(v)}
            textAnchor="end"
            dominantBaseline="middle"
            fill="var(--text-3)"
            fontSize={10}
            fontFamily="system-ui, sans-serif"
          >
            ${(v / 1000).toFixed(0)}k
          </text>
        ))}

        {/* X-axis labels */}
        {xTickMonths.map((m) => (
          <text
            key={m}
            x={xScale(m)}
            y={H - PADDING.bottom + 20}
            textAnchor="middle"
            fill="var(--text-3)"
            fontSize={10}
            fontFamily="system-ui, sans-serif"
          >
            {m === 1 ? 'Start' : `Yr ${m / 12}`}
          </text>
        ))}

        {/* Hover overlay */}
        <rect
          x={PADDING.left}
          y={PADDING.top}
          width={chartW}
          height={chartH}
          fill="transparent"
          onMouseMove={(e) => {
            const svg = e.currentTarget.ownerSVGElement!
            const pt = svg.createSVGPoint()
            pt.x = e.clientX
            pt.y = e.clientY
            const svgPt = pt.matrixTransform(svg.getScreenCTM()!.inverse())
            const m = Math.round(((svgPt.x - PADDING.left) / chartW) * (totalMonths - 1)) + 1
            setHoveredMonth(Math.max(1, Math.min(totalMonths, m)))
          }}
          onMouseLeave={() => setHoveredMonth(null)}
        />

        {/* Hover crosshair */}
        {hoveredData && (
          <>
            <line
              x1={xScale(hoveredData.month)}
              y1={PADDING.top}
              x2={xScale(hoveredData.month)}
              y2={PADDING.top + chartH}
              stroke="var(--text-3)"
              strokeWidth={0.5}
              strokeDasharray="4 2"
              opacity={0.5}
            />
            <circle
              cx={xScale(hoveredData.month)}
              cy={yScale(hoveredData.cumulative)}
              r={5}
              fill="#3b82f6"
              stroke="var(--surface-1)"
              strokeWidth={2}
            />
          </>
        )}
      </svg>

      {/* Hover tooltip */}
      {hoveredData && (
        <div
          className="pointer-events-none absolute rounded-lg border border-[var(--border)] bg-[var(--surface-1)] px-3 py-2 shadow-lg"
          style={{
            left: `${(xScale(hoveredData.month) / W) * 100}%`,
            top: `${(yScale(hoveredData.cumulative) / H) * 100 - 14}%`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <p className="text-xs font-semibold text-[var(--text-1)]">
            Month {hoveredData.month} · {hoveredData.rank}
          </p>
          <p className="text-[10px] text-[var(--text-3)]">
            ${hoveredData.monthlyTotal.toLocaleString()}/mo · ${hoveredData.cumulative.toLocaleString()} total
          </p>
          {hoveredData.event && (
            <p className="mt-0.5 text-[10px] font-medium text-violet-500">{hoveredData.event}</p>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Select helper ─────────────────────────────────────── */
function SelectField({
  label, value, onChange, options,
}: {
  label: string; value: string; onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--text-3)]">
        {label}
      </span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-xl border border-[var(--border)] bg-[var(--surface-2)] px-4 py-3 pr-10 text-sm font-medium text-[var(--text-1)] shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-3)]" />
      </div>
    </label>
  )
}

/* ── Main component ────────────────────────────────────── */
export default function ContractModeler() {
  const [rank, setRank] = useState('E-3')
  const [yos, setYos] = useState(0)
  const [zip, setZip] = useState('76544')
  const [hasDependents, setHasDependents] = useState(false)
  const [contractYears, setContractYears] = useState(4)
  const [serviceBranch, setServiceBranch] = useState<'army' | 'navy' | 'air_force' | 'marines' | 'coast_guard' | 'space_force'>('army')
  const [receivesBAH, setReceivesBAH] = useState(false)
  const [bahManuallySet, setBahManuallySet] = useState(false)
  const [bonuses, setBonuses] = useState<{ label: string; amount: number; month: number; taxFree?: boolean }[]>([])
  const [promotions, setPromotions] = useState<ContractPromotion[]>([])
  const [specialPays, setSpecialPays] = useState<SpecialPay[]>([])
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Smart BAH default: E-5 and below without dependents → off; otherwise → on
  // Only auto-set when the user hasn't manually toggled it
  useEffect(() => {
    if (bahManuallySet) return
    const gradeNum = parseInt(rank.split('-')[1], 10)
    const isJuniorEnlisted = rank.startsWith('E') && gradeNum <= 5
    setReceivesBAH(!(isJuniorEnlisted && !hasDependents))
  }, [rank, hasDependents, bahManuallySet])

  const [result, setResult] = useState<ContractResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const totalMonths = contractYears * 12

  const runCalc = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const input: ContractInput = {
        rank,
        yearsOfService: yos,
        zip,
        hasDependents,
        contractYears,
        serviceBranch,
        receivesBAH: receivesBAH,
        bonuses: bonuses.filter((b) => b.amount > 0),
        promotions,
        specialPays: specialPays.filter((s) => s.amount > 0),
      }
      const res = await calculateContractValue(input)
      setResult(res)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Calculation failed')
    } finally {
      setLoading(false)
    }
  }, [rank, yos, zip, hasDependents, contractYears, serviceBranch, receivesBAH, bonuses, promotions, specialPays])

  const [heroMode, setHeroMode] = useState<'total' | 'annual'>('total')

  // Auto-calculate on mount
  useEffect(() => { runCalc() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--text-1)]">Total Contract Modeler</h1>
        <p className="mt-1 text-sm text-[var(--text-3)]">
          See the total value of your enlistment contract — base pay, allowances, bonuses, and promotions over time.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-[380px_1fr]">
        {/* ── Left panel: inputs ─────────────────────── */}
        <div className="space-y-5">
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
            <h2 className="mb-4 flex items-center gap-2 text-sm font-bold text-[var(--text-1)]">
              <FileText className="h-4 w-4 text-blue-500" /> Contract Details
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  label="Starting Rank"
                  value={rank}
                  onChange={setRank}
                  options={RANKS.map((r) => ({ value: r, label: r }))}
                />
                <SelectField
                  label="Starting YOS"
                  value={String(yos)}
                  onChange={(v) => setYos(Number(v))}
                  options={Array.from({ length: 30 }, (_, i) => ({
                    value: String(i),
                    label: `${i} yr${i !== 1 ? 's' : ''}`,
                  }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  label="Contract Length"
                  value={String(contractYears)}
                  onChange={(v) => setContractYears(Number(v))}
                  options={CONTRACT_YEARS.map((y) => ({
                    value: String(y),
                    label: `${y} years`,
                  }))}
                />
                <SelectField
                  label="Branch"
                  value={serviceBranch}
                  onChange={(v) => setServiceBranch(v as typeof serviceBranch)}
                  options={SERVICE_BRANCHES}
                />
              </div>

              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--text-3)]">
                  Duty Station ZIP
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-2)] px-4 py-3 text-sm font-medium text-[var(--text-1)] shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  placeholder="e.g. 76544"
                />
              </label>

              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-3)]">
                  Dependents
                </span>
                <div className="flex overflow-hidden rounded-lg border border-[var(--border)]">
                  {[false, true].map((val) => (
                    <button
                      key={String(val)}
                      onClick={() => setHasDependents(val)}
                      className={`px-4 py-2 text-xs font-semibold transition ${
                        hasDependents === val
                          ? 'bg-blue-600 text-white'
                          : 'bg-[var(--surface-2)] text-[var(--text-3)] hover:text-[var(--text-1)]'
                      }`}
                    >
                      {val ? 'Yes' : 'No'}
                    </button>
                  ))}
                </div>
              </div>

              {/* BAH toggle */}
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-3)]">
                  Receives BAH
                </span>
                <div className="flex overflow-hidden rounded-lg border border-[var(--border)]">
                  {[true, false].map((val) => (
                    <button
                      key={String(val)}
                      onClick={() => { setReceivesBAH(val); setBahManuallySet(true) }}
                      className={`px-4 py-2 text-xs font-semibold transition ${
                        receivesBAH === val
                          ? 'bg-blue-600 text-white'
                          : 'bg-[var(--surface-2)] text-[var(--text-3)] hover:text-[var(--text-1)]'
                      }`}
                    >
                      {val ? 'Yes' : 'No'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Promotions */}
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-[var(--text-1)]">
              <Award className="h-4 w-4 text-violet-500" /> Promotions
            </h2>

            {promotions.map((promo, idx) => (
              <div key={idx} className="mb-2 flex items-center gap-2">
                <div className="relative w-24">
                  <select
                    value={promo.newRank}
                    onChange={(e) => {
                      const next = [...promotions]
                      next[idx] = { ...next[idx], newRank: e.target.value }
                      setPromotions(next)
                    }}
                    className="w-full appearance-none rounded-xl border border-[var(--border)] bg-[var(--surface-2)] px-4 py-3 pr-8 text-sm font-medium text-[var(--text-1)] shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    {RANKS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-3)]" />
                </div>
                <div className="relative flex-1">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--text-3)]">
                    Mo
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={totalMonths}
                    value={promo.month}
                    onChange={(e) => {
                      const next = [...promotions]
                      next[idx] = { ...next[idx], month: Number(e.target.value) }
                      setPromotions(next)
                    }}
                    className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-2)] py-3 pl-9 pr-3 text-sm font-medium text-[var(--text-1)] shadow-sm focus:outline-none"
                  />
                </div>
                <button
                  onClick={() => setPromotions(promotions.filter((_, i) => i !== idx))}
                  className="rounded-md p-1.5 text-[var(--text-3)] hover:bg-red-500/10 hover:text-red-500"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}

            <button
              onClick={() =>
                setPromotions([...promotions, { month: 24, newRank: rank === 'E-3' ? 'E-4' : 'E-5' }])
              }
              className="flex items-center gap-1.5 rounded-lg border border-dashed border-[var(--border)] px-3 py-2 text-xs font-medium text-[var(--text-3)] transition hover:border-violet-500/30 hover:text-violet-500"
            >
              <Plus className="h-3.5 w-3.5" /> Add Promotion
            </button>
          </div>

          {/* Bonuses */}
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-[var(--text-1)]">
              <DollarSign className="h-4 w-4 text-emerald-500" /> Bonuses
            </h2>

            {bonuses.map((b, idx) => (
              <div key={idx} className="mb-3 rounded-lg border border-[var(--border)] bg-[var(--surface-2)]/50 p-3">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={b.label}
                    onChange={(e) => {
                      const next = [...bonuses]
                      next[idx] = { ...next[idx], label: e.target.value }
                      setBonuses(next)
                    }}
                    className="flex-1 rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2.5 text-sm font-medium text-[var(--text-1)] focus:outline-none"
                    placeholder="Label"
                  />
                  <button
                    onClick={() => setBonuses(bonuses.filter((_, i) => i !== idx))}
                    className="rounded-md p-1.5 text-[var(--text-3)] hover:bg-red-500/10 hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="relative flex-1">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-[var(--text-3)]">
                      $
                    </span>
                    <input
                      type="number"
                      value={b.amount || ''}
                      onChange={(e) => {
                        const next = [...bonuses]
                        next[idx] = { ...next[idx], amount: Number(e.target.value) }
                        setBonuses(next)
                      }}
                      className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] py-2.5 pl-7 pr-3 text-base font-bold text-[var(--text-1)] focus:outline-none"
                      placeholder="10,000"
                    />
                  </div>
                  <label className="flex cursor-pointer items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={b.taxFree ?? false}
                      onChange={(e) => {
                        const next = [...bonuses]
                        next[idx] = { ...next[idx], taxFree: e.target.checked }
                        setBonuses(next)
                      }}
                      className="h-4 w-4 rounded border-[var(--border)] text-blue-500 focus:ring-blue-500/20"
                    />
                    <span className="text-[11px] font-medium text-[var(--text-3)]">Tax-free</span>
                  </label>
                </div>
              </div>
            ))}

            <button
              onClick={() =>
                setBonuses([...bonuses, { label: 'Signing Bonus', amount: 10000, month: 1, taxFree: false }])
              }
              className="flex items-center gap-1.5 rounded-lg border border-dashed border-[var(--border)] px-3 py-2 text-xs font-medium text-[var(--text-3)] transition hover:border-emerald-500/30 hover:text-emerald-500"
            >
              <Plus className="h-3.5 w-3.5" /> Add Bonus
            </button>
            {bonuses.length > 0 && (
              <p className="mt-2 text-[10px] leading-relaxed text-[var(--text-3)]">
                Bonuses pay 50% upfront at the specified month, with the remaining 50% split equally across the rest of the contract.
              </p>
            )}
          </div>

          {/* Special pays (collapsible) */}
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex w-full items-center justify-between text-sm font-bold text-[var(--text-1)]"
            >
              Special & Incentive Pays
              {showAdvanced ? (
                <ChevronDown className="h-4 w-4 rotate-180 text-[var(--text-3)]" />
              ) : (
                <ChevronDown className="h-4 w-4 text-[var(--text-3)]" />
              )}
            </button>

            {showAdvanced && (
              <div className="mt-3 space-y-2">
                {specialPays.map((sp, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={sp.label}
                      onChange={(e) => {
                        const next = [...specialPays]
                        next[idx] = { ...next[idx], label: e.target.value }
                        setSpecialPays(next)
                      }}
                      className="flex-1 rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-xs text-[var(--text-1)] focus:outline-none"
                    />
                    <div className="relative w-20">
                      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[var(--text-3)]">$</span>
                      <input
                        type="number"
                        value={sp.amount || ''}
                        onChange={(e) => {
                          const next = [...specialPays]
                          next[idx] = { ...next[idx], amount: Number(e.target.value) }
                          setSpecialPays(next)
                        }}
                        className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] py-2 pl-6 pr-2 text-xs text-[var(--text-1)] focus:outline-none"
                      />
                    </div>
                    <button
                      onClick={() => setSpecialPays(specialPays.filter((_, i) => i !== idx))}
                      className="rounded-md p-1 text-[var(--text-3)] hover:bg-red-500/10 hover:text-red-500"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {SPECIAL_PAY_PRESETS.filter(
                    (p) => !specialPays.some((sp) => sp.label === p.label)
                  ).map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => setSpecialPays([...specialPays, { ...preset }])}
                      className="flex items-center gap-1 rounded-full border border-[var(--border)] px-2.5 py-1 text-[10px] font-medium text-[var(--text-3)] transition hover:border-blue-500/30 hover:text-blue-500"
                    >
                      <Plus className="h-3 w-3" /> {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Calculate */}
          <button
            onClick={runCalc}
            disabled={loading || zip.length < 5}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <TrendingUp className="h-4 w-4" />
            )}
            Model Contract
          </button>

          {error && (
            <p className="rounded-lg bg-red-500/10 px-4 py-2 text-xs text-red-500">{error}</p>
          )}
        </div>

        {/* ── Right panel: results ───────────────────── */}
        <div className="space-y-6">
          {result && (
            <>
              {/* Big number hero — click to toggle */}
              <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-8 text-center shadow-sm">
                <button
                  onClick={() => setHeroMode(heroMode === 'total' ? 'annual' : 'total')}
                  className="group mx-auto cursor-pointer"
                >
                  <p className="text-xs font-semibold uppercase tracking-widest text-[var(--text-3)] transition group-hover:text-blue-500">
                    {heroMode === 'total' ? 'Total Contract Value' : 'Average Per Year'}
                  </p>
                  <p className="mt-2 text-5xl font-extrabold tracking-tight text-[var(--text-1)] sm:text-6xl">
                    <AnimatedBigNumber
                      value={
                        heroMode === 'total'
                          ? result.grandTotal
                          : Math.round(result.grandTotal / contractYears)
                      }
                    />
                  </p>
                  <p className="mt-1 text-sm text-[var(--text-3)]">
                    {heroMode === 'total'
                      ? `over ${contractYears} year${contractYears !== 1 ? 's' : ''}`
                      : `per year · ${contractYears}-year contract`}
                  </p>
                  <p className="mt-2 text-[10px] font-medium text-[var(--text-3)] opacity-0 transition group-hover:opacity-100">
                    Click to show {heroMode === 'total' ? 'annual average' : 'total value'}
                  </p>
                </button>

                <div className="mt-6 flex items-center justify-center gap-8">
                  <div>
                    <p className="text-xs text-[var(--text-3)]">Monthly Avg</p>
                    <p className="text-lg font-bold text-[var(--text-1)]">
                      ${Math.round(result.grandTotal / (contractYears * 12)).toLocaleString()}
                    </p>
                  </div>
                  <div className="h-8 w-px bg-[var(--border)]" />
                  <div>
                    <p className="text-xs text-[var(--text-3)]">Civilian Equivalent</p>
                    <p className="text-lg font-bold text-emerald-500">
                      ${result.civilianEquivalent.toLocaleString()}
                    </p>
                  </div>
                  {result.state && (
                    <>
                      <div className="h-8 w-px bg-[var(--border)]" />
                      <div>
                        <p className="text-xs text-[var(--text-3)]">State</p>
                        <p className="text-lg font-bold text-[var(--text-1)]">{result.state}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Chart */}
              <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
                <h2 className="mb-4 text-sm font-bold text-[var(--text-1)]">
                  Cumulative Earnings
                </h2>
                <CumulativeChart result={result} contractYears={contractYears} />
              </div>

              {/* Monthly breakdown highlights */}
              <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
                <h2 className="mb-4 text-sm font-bold text-[var(--text-1)]">Pay at a Glance</h2>
                <div className="grid gap-3 sm:grid-cols-3">
                  {[
                    { label: 'Start', data: result.months[0] },
                    ...(result.months.length > 12
                      ? [{ label: 'Mid-Contract', data: result.months[Math.floor(result.months.length / 2) - 1] }]
                      : []),
                    { label: 'End', data: result.months[result.months.length - 1] },
                  ].map(({ label, data }) => (
                    <div
                      key={label}
                      className="rounded-xl border border-[var(--border)] bg-[var(--surface-2)]/50 p-4"
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-3)]">
                        {label} — Mo {data.month}
                      </p>
                      <p className="mt-1 text-lg font-bold text-[var(--text-1)]">
                        ${data.monthlyTotal.toLocaleString()}<span className="text-xs font-normal text-[var(--text-3)]">/mo</span>
                      </p>
                      <p className="mt-0.5 text-xs text-[var(--text-3)]">
                        {data.rank} · {data.yos} YOS
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
